import type { FC } from 'react';
import { FileList, FileListButton, Flex, Heading, Inline } from '@george-labs.com/design-system';
import type { FileProps } from '@george-labs.com/design-system/types/components/Input/FileInput/components';

import { formatDate } from '@luna/date';
import { sanitize } from '@luna/utils';
import { t } from '@luna/i18n';
import { downloadFile } from '@shared/file';

import type { BankMessage } from '../BankMessagesTypes';
import { MESSAGE_ALLOWED_TAGS, getFileListItems } from './BankMessageService';
import style from './BankMessageView.module.scss';
import { getBankMessageUrl } from '../BankMessagesService';

interface Props {
    message: BankMessage;
    onAttachmentDownload(messageId: string, attachmentId: string): void;
}

export const BankMessageContent: FC<Props> = ({ message, onAttachmentDownload }) => {
    const handleAttachmentDownload = (messageId: string, attachmentId: string | undefined) => {
        if (!attachmentId) {
            return;
        }

        onAttachmentDownload?.(messageId, attachmentId);
        downloadFile({ url: `${getBankMessageUrl(messageId)}/attachments/${attachmentId}` });
    };

    const renderDownloadButton = (file: FileProps) => (
        <FileListButton
            icon={FileListButton.ICON.FILE_DOWNLOAD}
            onClick={() => handleAttachmentDownload(message.id, file.id)}
        >
            {t('bankMessages.details.downloadAttachmentLabel_a11y')}
        </FileListButton>
    );
    return (
        <Flex direction="column">
            <Inline data-cy="message-date" small faded className="align-self-end">
                {formatDate(message.date)}
            </Inline>
            <Heading data-cy="message-subject" level={2}>
                {message.subject}
            </Heading>
            <div
                data-cy="message-body"
                className={style.messageBody}
                dangerouslySetInnerHTML={{
                    __html: sanitize(message.body, {
                        ALLOWED_TAGS: MESSAGE_ALLOWED_TAGS,
                    }),
                }}
            />
            {!!message.attachments?.length && (
                <FileList
                    data-cy="message-attachments"
                    className="mt-3"
                    showRemoveButton={false}
                    files={getFileListItems(message.attachments)}
                    renderExtraButtons={renderDownloadButton}
                />
            )}
        </Flex>
    );
};
